<script setup>
import { computed } from "vue";

function hashString(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) { 
    h ^= str.charCodeAt(i); 
    h = Math.imul(h, 16777619); 
  }
  return h >>> 0;
}

function seededRandom(seed) {
  let t = seed + 0x6D2B79F5;
  return function() { 
    t |= 0; 
    t = Math.imul(t ^ t >>> 15, t | 1); 
    t ^= t + Math.imul(t ^ t >>> 7, t | 61); 
    return ((t ^ t >>> 14) >>> 0) / 4294967296; 
  };
}

function jaccardSimilarity(a, b) {
  const A = new Set(a), B = new Set(b);
  const inter = [...A].filter(x => B.has(x)).length;
  const union = new Set([...A, ...B]).size || 1;
  return inter / union;
}

function tokenize(text) { 
  return (text || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean); 
}

function smallModel(prompt, tools = []) {
  const seed = hashString(prompt + "|" + tools.join(","));
  const rand = seededRandom(seed);
  const p = prompt.toLowerCase();
  if (p.includes("email")) return "Subject: Welcome!\n\nHi there,\n\nThanks for joining us. Let me know if you have any questions.\n\nBest,";
  if (p.includes("summarize")) { 
    const clipped = prompt.slice(0, 140).replace(/\s+/g, " "); 
    return "Summary: " + clipped + (prompt.length > 140 ? "..." : ""); 
  }
  if (p.includes("steps") || p.includes("how to")) return "- Step 1: Analyze\n- Step 2: Use tools (" + (tools.join(", ") || "none") + ")\n- Step 3: Output";
  const stock = [
    "Here is a concise response addressing the key points.",
    "Outline constraints, options, and a recommendation.",
    "Clarify assumptions and provide a direct answer."
  ];
  return stock[Math.floor(rand() * stock.length)];
}

let payload = null;
try { 
  const raw = localStorage.getItem("preview_eval_payload"); 
  payload = raw ? JSON.parse(raw) : null; 
} catch {}

const results = computed(() => {
  if (!payload) return null;
  return payload.tasks.map((t, idx) => {
    const modelOut = smallModel(t.prompt, t.tools || []);
    const gold = t.answer || "";
    const sim = jaccardSimilarity(tokenize(modelOut), tokenize(gold));
    const pass = sim >= 0.5;
    return { idx: idx + 1, id: t.id, prompt: t.prompt, gold, tools: t.tools || [], modelOut, similarity: sim, pass };
  });
});
</script>

<template>
  <div class="preview-results-shell">
    <div v-if="!payload" class="preview-results-header">
      <h1>Preview Results</h1>
      <div class="muted">Nothing to preview. Go back and click "Preview Results".</div>
    </div>
    <template v-else>
      <div class="preview-results-header">
        <h1>Preview Results • {{ payload.evalName }}</h1>
        <div class="muted">Environment: Tiny Open Source Model (mock) • Threshold: 0.50 Jaccard</div>
      </div>
      <div class="table">
        <div class="thead">
          <div>#</div>
          <div>Prompt</div>
          <div>Model Output</div>
          <div>Gold Answer</div>
          <div>Tools</div>
          <div>Score</div>
          <div>Status</div>
        </div>
        <div class="tbody">
          <div v-for="r in results" :key="r.id" class="trow">
            <div class="cell index">#{{ r.idx }}</div>
            <div class="cell prompt">{{ r.prompt || '—' }}</div>
            <div class="cell model"><pre>{{ r.modelOut }}</pre></div>
            <div class="cell gold"><pre>{{ r.gold || '—' }}</pre></div>
            <div class="cell tools">{{ r.tools.length ? r.tools.join(', ') : '—' }}</div>
            <div class="cell score">{{ r.similarity.toFixed(2) }}</div>
            <div :class="['cell', 'status', r.pass ? 'ok' : 'fail']">{{ r.pass ? 'Pass' : 'Fail' }}</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

